import { useCallback, useEffect, useRef, useState } from 'react';
import { userPreferencesService } from '@services/userPreferencesService';

/**
 * Carrega e persiste os filtros do usuário em [Financial].[UserPreferences].
 *
 * `loaded` só vira true depois que a leitura terminou (inclusive quando não há
 * preferência salva ou a chamada falha). As telas devem esperar por ele antes
 * de disparar a busca de dados, senão a tela carrega uma vez com os filtros
 * default e de novo com os filtros restaurados.
 *
 * `save` é fire-and-forget: um erro ao gravar preferência nunca deve quebrar a
 * tela nem exibir mensagem de erro ao usuário.
 *
 * O nome da entidade é gravado em `PreferenceEntity`, um nvarchar(30) — mantenha
 * as constantes abaixo dentro desse limite. `PreferenceParameters` é nvarchar(1000),
 * então persista apenas identificadores, nunca objetos inteiros.
 */
export function useUserPreferences<T extends Record<string, unknown>>(entity: string) {
  const [prefs, setPrefs] = useState<T | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    userPreferencesService
      .getPreferences<T>(entity)
      .then((p) => {
        if (cancelled) return;
        setPrefs(p);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [entity]);

  // Evita reescrever a mesma preferência a cada render/refetch.
  const lastSaved = useRef<string | null>(null);

  const save = useCallback(
    (parameters: T) => {
      const serialized = JSON.stringify(parameters);
      if (lastSaved.current === serialized) return;
      lastSaved.current = serialized;
      userPreferencesService.savePreferences(entity, parameters).catch(() => {});
    },
    [entity],
  );

  return { prefs, loaded, save };
}

/** Entidades de preferência das telas do módulo Financeiro (máx. 30 caracteres). */
export const PREF_FIN_CHECKING_ACCOUNT = 'FIN_CHECKING_ACCOUNT';
export const PREF_FIN_CREDIT_CARDS = 'FIN_CREDIT_CARDS';
export const PREF_FIN_RECURRING = 'FIN_RECURRING';
export const PREF_FIN_IMPORT = 'FIN_TRANSACTION_IMPORT';
export const PREF_FIN_REPORT_REVENUE = 'FIN_REPORT_REVENUE';
export const PREF_FIN_REPORT_EXPENSES = 'FIN_REPORT_EXPENSES';
export const PREF_FIN_REPORT_DRE = 'FIN_REPORT_DRE';
export const PREF_FIN_REPORT_CASHFLOW = 'FIN_REPORT_CASHFLOW';
export const PREF_FIN_REPORT_SUPPLIERS = 'FIN_REPORT_SUPPLIERS';
